import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { GrupoService } from '../grupo/grupo.service';
import { ProdutosService } from '../produtos/produto.service';
import { ApiService } from '../api.service';
import { GrupoDTO, GrupoResponseDTO } from '../../models/grupo.model';
import { ProdutosDTO, ProdutosResponseDTO } from '../../models/produto.models';
import {
  CardapioMinishopCategoriaDTO,
  CardapioMinishopProdutoDTO,
  CardapioMinishopProdutoFormDTO,
} from '../../models/cardapio-minishop/cardapio-minishop.model';
import { CardapioConsultaDTO } from '../../models/cardapio-digital/cardapio-digital.model';

@Injectable({
  providedIn: 'root',
})
export class CardapioMinishopService {

  /**
   * Flag para alternar entre dados mock e API real.
   * Quando true, o componente deve usar getDadosMock() em vez de chamar a API.
   * Em producao, deve ser false.
   */
  usarMock = false;

  constructor(
    private grupoService: GrupoService,
    private produtosService: ProdutosService,
    private apiService: ApiService
  ) {}

  // --- Categorias (usa GrupoService) ---

  listarCategorias(): Observable<GrupoResponseDTO> {
    return this.grupoService.get('/grupo', { page: 0, size: 100, sort: 'id,asc' });
  }

  criarCategoria(nome: string): Observable<GrupoDTO> {
    return this.grupoService.post('/grupo', { nome, status: true } as GrupoDTO);
  }

  atualizarCategoria(id: number, data: Partial<GrupoDTO>): Observable<GrupoDTO> {
    return this.grupoService.put(`/grupo/${id}`, data as GrupoDTO);
  }

  excluirCategoria(id: number): Observable<any> {
    return this.grupoService.delete(`/grupo/${id}`);
  }

  // --- Produtos (usa ProdutosService) ---

  listarTodosProdutos(): Observable<ProdutosResponseDTO> {
    return this.produtosService.get('/item', { page: 0, size: 500, sort: 'id,asc' });
  }

  listarProdutosPorGrupo(grupoId: number): Observable<any> {
    return this.produtosService.getItensPorGrupo(grupoId, 0, 100);
  }

  criarProduto(data: ProdutosDTO): Observable<ProdutosDTO> {
    return this.produtosService.post('/item', data);
  }

  atualizarProduto(id: number, data: ProdutosDTO): Observable<ProdutosDTO> {
    return this.produtosService.put(`/item/${id}`, data);
  }

  excluirProduto(id: number): Observable<any> {
    return this.produtosService.delete(`/item/${id}`);
  }

  // --- Preview do cardapio ---

  carregarPreview(): Observable<CardapioConsultaDTO> {
    return this.apiService.read<CardapioConsultaDTO>('/cardapio/preview');
  }

  // --- Mapeamento: GrupoDTO + ProdutosDTO[] → CardapioMinishopCategoriaDTO ---

  mapGrupoToCategoria(grupo: GrupoDTO, produtos: ProdutosDTO[], index: number): CardapioMinishopCategoriaDTO {
    return {
      id: grupo.id,
      nome: grupo.nome,
      ordem: index,
      visivel: grupo.status,
      destaque: false,
      produtos: produtos.map((p, i) => this.mapProdutoToCardapio(p, i)),
    };
  }

  // --- Mapeamento: ProdutosDTO → CardapioMinishopProdutoDTO ---

  mapProdutoToCardapio(produto: ProdutosDTO, index: number): CardapioMinishopProdutoDTO {
    return {
      id: produto.id,
      nome: produto.nome,
      descricao: null,
      preco: produto.precoDeVenda,
      precoPromocional: produto.desconto > 0
        ? produto.precoDeVenda - produto.desconto
        : null,
      foto: produto.imagem || null,
      fotoUrl: produto.imagem || null,
      visivel: produto.status && (produto.habilitarMiniShopping ?? false),
      ordem: index,
      grupo_id: produto.grupo_id,
      habilitarMiniShopping: produto.habilitarMiniShopping ?? false,
      controleEstoque: false,
      estoqueMinimo: produto.estoqueMinimo,
      saldo: produto.saldo,
      // `GET /item` devolve `ItemConsultaDTO`, que traz as impressoras como OBJETO
      // (`impressoraDTO`/`impressoraPedidoDTO`) e nao como id escalar. Ler so o escalar
      // trazia `null` e, no save, apagava o vinculo do item.
      impressoraPadraoId: produto.impressoraPadraoId ?? produto.impressoraDTO?.id ?? null,
      impressoraPadraoPedidoId: produto.impressoraPadraoPedidoId ?? produto.impressoraPedidoDTO?.id ?? null,
      permiteImprimir: produto.permiteImprimir ?? false,
      permiteImprimirPedido: produto.permiteImprimirPedido ?? false,
      variantes: [],
      modificadores: [],
      tipoPreco: 'SIMPLES',
    };
  }

  // --- Mapeamento: FormDTO → ProdutosDTO para envio ao backend ---

  // --- Dados mock para teste/demonstracao sem backend ---

  getDadosMock(): CardapioMinishopCategoriaDTO[] {
    return [
      {
        id: 1, nome: 'Lanches', ordem: 0, visivel: true, destaque: false,
        produtos: [
          {
            id: 1, nome: 'Bacon Burguer',
            descricao: 'Hamburguesa con carne 180g, bacon crocante, queso cheddar y salsa BBQ.',
            preco: 50000, precoPromocional: 42000, visivel: true, ordem: 0, grupo_id: 1,
            habilitarMiniShopping: true, controleEstoque: false, estoqueMinimo: 0, saldo: 0,
            variantes: [], modificadores: [], tipoPreco: 'SIMPLES',
          },
          {
            id: 2, nome: 'Gula Clasica',
            descricao: 'Hamburguesa con carne 180g, lechuga, tomate, queso y salsa especial.',
            preco: 45000, visivel: true, ordem: 1, grupo_id: 1,
            habilitarMiniShopping: true, controleEstoque: false, estoqueMinimo: 0, saldo: 0,
            variantes: [], modificadores: [], tipoPreco: 'SIMPLES',
          },
          {
            id: 3, nome: 'Gulosao Doble',
            descricao: 'Doble carne 360g, doble queso, bacon, huevo frito y salsa especial.',
            preco: 65000, visivel: true, ordem: 2, grupo_id: 1,
            habilitarMiniShopping: true, controleEstoque: false, estoqueMinimo: 0, saldo: 0,
            variantes: [], modificadores: [], tipoPreco: 'SIMPLES',
          },
        ],
      },
      {
        id: 2, nome: 'Bebidas', ordem: 1, visivel: true, destaque: false,
        produtos: [
          {
            id: 10, nome: 'Coca Cola Lata 354ml',
            descricao: 'Coca-Cola clasica en lata bien fria.',
            preco: 12000, visivel: true, ordem: 0, grupo_id: 2,
            habilitarMiniShopping: true, controleEstoque: false, estoqueMinimo: 0, saldo: 0,
            variantes: [], modificadores: [], tipoPreco: 'SIMPLES',
          },
          {
            id: 11, nome: 'Agua Mineral 500ml',
            descricao: 'Agua mineral natural.',
            preco: 8000, visivel: true, ordem: 1, grupo_id: 2,
            habilitarMiniShopping: true, controleEstoque: false, estoqueMinimo: 0, saldo: 0,
            variantes: [], modificadores: [], tipoPreco: 'SIMPLES',
          },
          {
            id: 12, nome: 'Jugo del Valle',
            descricao: 'Jugo natural de frutas, varios sabores.',
            preco: 10000, visivel: true, ordem: 2, grupo_id: 2,
            habilitarMiniShopping: true, controleEstoque: false, estoqueMinimo: 0, saldo: 0,
            variantes: [], modificadores: [], tipoPreco: 'SIMPLES',
          },
        ],
      },
      {
        id: 3, nome: 'Sobremesas', ordem: 2, visivel: true, destaque: false,
        produtos: [
          {
            id: 20, nome: 'Brownie con Helado',
            descricao: 'Brownie de chocolate caliente con helado de vainilla y salsa de chocolate.',
            preco: 28000, precoPromocional: 22000, visivel: true, ordem: 0, grupo_id: 3,
            habilitarMiniShopping: true, controleEstoque: false, estoqueMinimo: 0, saldo: 0,
            variantes: [], modificadores: [], tipoPreco: 'SIMPLES',
          },
          {
            id: 21, nome: 'Helado Artesanal',
            descricao: 'Helado cremoso artesanal, elige tu sabor favorito.',
            preco: 18000, visivel: true, ordem: 1, grupo_id: 3,
            habilitarMiniShopping: true, controleEstoque: false, estoqueMinimo: 0, saldo: 0,
            variantes: [], modificadores: [], tipoPreco: 'SIMPLES',
          },
          {
            id: 22, nome: 'Flan Casero',
            descricao: 'Flan casero con dulce de leche y crema.',
            preco: 20000, visivel: true, ordem: 2, grupo_id: 3,
            habilitarMiniShopping: true, controleEstoque: false, estoqueMinimo: 0, saldo: 0,
            variantes: [], modificadores: [], tipoPreco: 'SIMPLES',
          },
        ],
      },
    ];
  }

  mapFormToProdutoDTO(form: CardapioMinishopProdutoFormDTO, grupoId: number, produtoOriginal?: ProdutosDTO): ProdutosDTO {
    // O backend recebe enums como string (ex: "IVA_ISENTO"), mas o GET retorna objetos { enum, nome, ... }
    // Por isso é necessario extrair o campo .enum antes de enviar
    const tipoDeImposto = produtoOriginal?.tipoDeImposto?.enum || produtoOriginal?.tipoDeImposto || 'IVA_ISENTO';
    const tipoDeDesconto = produtoOriginal?.tipoDeDesconto?.enum || produtoOriginal?.tipoDeDesconto || 'SEM_DESCONTO';
    const tipoItem: any = produtoOriginal?.tipoItem?.enum || produtoOriginal?.tipoItem || 'MERCADORIA';

    return {
      ...(produtoOriginal || {} as ProdutosDTO),
      nome: form.nome,
      precoDeVenda: form.preco || 0,
      desconto: (form.preco && form.precoPromocional)
        ? form.preco - form.precoPromocional
        : 0,
      grupo_id: grupoId,
      status: form.visivel,
      habilitarMiniShopping: true,
      imagem: form.fotoBase64 || form.foto || undefined,
      estoqueMinimo: 0,
      saldo: 0,
      custo: produtoOriginal?.custo || 0,
      unidadeMedidaDeItem_id: produtoOriginal?.unidadeMedidaDeItem_id || 1,
      tipoDeImposto,
      tipoDeDesconto,
      tipoItem,
      // `PUT /item/{id}` e SUBSTITUICAO TOTAL: campo ausente no payload vira o default do
      // `ItemDTO` (`false`/`null`) e apaga o que existia. Esta tela nao edita impressao,
      // entao devolve os quatro campos como vieram do item.
      // `undefined` some do JSON e o backend aplica o default (null) — mesmo efeito de "sem
      // impressora". O que nao pode e omitir quando o item TEM impressora.
      impressoraPadraoId: form.cozinha?.value ?? produtoOriginal?.impressoraPadraoId ?? undefined,
      impressoraPadraoPedidoId: produtoOriginal?.impressoraPadraoPedidoId ?? undefined,
      permiteImprimir: produtoOriginal?.permiteImprimir ?? false,
      permiteImprimirPedido: produtoOriginal?.permiteImprimirPedido ?? false,
    };
  }
}
